import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { CalendarCheckIcon, CheckIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { GoalDecisionCard } from "./components/GoalDecisionCard";
import type { Decision, Step } from "./types";
import { summarizeDecisions } from "./utils";
import { apiClient } from "../../lib/api";
import { addDays, rollingConsistency, todayString } from "../../lib/goal-stats";

export function PlanPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("review");
  const [goalIndex, setGoalIndex] = useState(0);
  const [decisions, setDecisions] = useState<Map<string, Decision>>(new Map());

  const today = todayString();
  const from = addDays(today, -7);
  const to = addDays(today, -1);

  const { data: goals } = useQuery({
    queryKey: ["daily-goals"],
    queryFn: () => apiClient.getDailyGoals(),
  });

  const { data: history } = useQuery({
    queryKey: ["goal-history", from, to],
    queryFn: () => apiClient.getDailyGoalHistory(from, to),
  });

  if (!goals || !history) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-7 h-7 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const activeGoals = goals.filter((goal) => goal.active);
  const consistencyByGoalId = new Map(
    history.goals.map((goal) => [goal.goalId, rollingConsistency(goal.days, 7)]),
  );
  const currentGoal = activeGoals[goalIndex];

  const recordDecision = (goalId: string, decision: Decision) => {
    setDecisions((prev) => new Map(prev).set(goalId, decision));
    if (goalIndex + 1 >= activeGoals.length) {
      setStep("done");
    } else {
      setGoalIndex((index) => index + 1);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
      <header className="flex items-center gap-3">
        <span className="shrink-0 w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
          <CalendarCheckIcon className="w-4.5 h-4.5 text-primary" />
        </span>
        <div>
          <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
            Weekly planning
          </p>
          <h1 className="text-xl font-semibold tracking-tight">Decide next week</h1>
        </div>
      </header>

      {activeGoals.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No active goals to plan.{" "}
          <Link to="/goals" className="text-primary font-medium hover:underline">
            Add some in Goals
          </Link>
          .
        </div>
      ) : (
        <>
          {step === "review" && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-border bg-card px-5 py-4 space-y-3">
                <div>
                  <h2 className="text-base font-semibold">Last week at a glance</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Just the facts — then one decision per goal.
                  </p>
                </div>
                <div className="divide-y divide-border/60">
                  {activeGoals.map((goal) => {
                    const pct = consistencyByGoalId.get(goal.id) ?? null;
                    return (
                      <div key={goal.id} className="flex items-center justify-between gap-3 py-2">
                        <span className="text-sm font-medium truncate">{goal.name}</span>
                        <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
                          {pct === null ? "not tracked" : `${pct}%`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <Button size="lg" className="w-full" onClick={() => setStep("decide")}>
                Start deciding
              </Button>
            </div>
          )}

          {step === "decide" && currentGoal && (
            <GoalDecisionCard
              key={currentGoal.id}
              goal={currentGoal}
              lastWeekPercent={consistencyByGoalId.get(currentGoal.id) ?? null}
              position={goalIndex + 1}
              total={activeGoals.length}
              onDecided={(decision) => {
                void queryClient.invalidateQueries({ queryKey: ["daily-goals"] });
                void queryClient.invalidateQueries({ queryKey: ["today-dashboard"] });
                recordDecision(currentGoal.id, decision);
              }}
            />
          )}

          {step === "done" && (
            <div className="rounded-2xl border border-border bg-accent/40 px-5 py-6 text-center space-y-3">
              <span className="mx-auto w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
                <CheckIcon className="w-4.5 h-4.5 text-primary" strokeWidth={3} />
              </span>
              <div>
                <p className="text-sm font-semibold">Next week is decided.</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {summarizeDecisions(decisions)}
                </p>
              </div>
              <Button variant="secondary" onClick={() => void navigate({ to: "/dashboard" })}>
                Back to dashboard
              </Button>
            </div>
          )}
        </>
      )}

      {step !== "done" && (
        <p className="text-center text-xs text-muted-foreground">
          <Link to="/dashboard" className="hover:text-foreground transition-colors">
            Not now — back to dashboard
          </Link>
        </p>
      )}
    </div>
  );
}
